import {
  AdmProject,
  AdmPlan,
  SubjectType,
  DateTimeInput,
  AllMemberTypes,
} from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DateTimeUtils, NumberUtils, UiUtils } from "@stellar/utils";

// [DOC]
// Functions in this file are intended to create strings
// out of specific objects and format them so they can be
// displayed in a human-readable form. Because these functions
// have a very similar purpose it makes sense to give them a
// consistent naming schema.
// That's why all functions in this file should be prefixed with "format"

/**
 * Returns a name to identify a plan. If the plan has a label, it will be used.
 * If not, the first assigned bundle- or feature-name is used.
 */
export function formatPlanName(plan: AdmPlan): string {
  if (plan.label) {
    return plan.label;
  }
  if (plan.featureBundles[0]?.name && plan.featureBundles[0].name !== "") {
    return plan.featureBundles[0].name;
  } else if (plan.features[0]?.name && plan.features[0].name !== "") {
    return plan.features[0].name;
  }
  return UiUtils.DASH;
}

export function getPlanAssigneeName(plan: AdmPlan): string {
  switch (plan.subject.type) {
    case "PROJECT":
    case "COMPANY": {
      return plan.subject.obj.name;
    }
    case "DEVICE": {
      return plan.subject.id;
    }
    case "COMPANY_MEMBER":
    case "USER": {
      return getMemberDisplayName(plan.subject.obj);
    }
    default: {
      return UiUtils.DASH;
    }
  }
}

/**
 * Return the member display name
 * Will check member's name in the order of first name, last name, name and email
 * If shouldAddEmailAtEnd is true, it will add member's email address at the end of the member's name in parentheses
 */
export function getMemberDisplayName(
  member: AllMemberTypes | FaroCoreApiTypes.UserAsManager,
  shouldAddEmailAtEnd: boolean = false
): string {
  let memberFullName: string = "";

  if (member.firstName && member.lastName) {
    memberFullName = `${member.firstName} ${member.lastName}`.trim();
  } else if (member.firstName) {
    memberFullName = member.firstName.trim();
  } else if (member.lastName) {
    memberFullName = member.lastName.trim();
  } else if ("name" in member && member.name) {
    memberFullName = member.name.trim();
  }

  if (memberFullName) {
    if (shouldAddEmailAtEnd) {
      return `${memberFullName} (${member.email})`.trim();
    } else {
      return memberFullName.trim();
    }
  } else if (member.email) {
    return member.email.trim();
  }

  return UiUtils.DASH;
}

/**
 * Returns a formatted string showing the project area with unit.
 */
export function formatProjectArea(projectArea: AdmProject["area"]): string {
  return `${NumberUtils.formatNumberWithSpaces(projectArea.amount)} ${
    projectArea.unit
  }`;
}

/**
 * Create a more readable string for sqft assigned to a group by separating the number in thousands
 * By definition, -1 means that there is no restriction on the area assigned to a group
 */
export function formatGroupSqftAssigned(area: number | undefined): string {
  if (!area) {
    return "unrestricted";
  }
  return NumberUtils.formatNumberWithSpaces(area);
}

/** Format the subject type into a human-readable string */
export function formatSubjectType(subjectType: SubjectType): string {
  switch (subjectType) {
    case "USER": {
      return "Member";
    }
    case "COMPANY_MEMBER": {
      return "Workspace Member";
    }
    case "COMPANY": {
      return "Workspace";
    }
    case "PROJECT": {
      return "Project";
    }
    case "DEVICE": {
      // Note: since we don't have a device name and the summary page currently displays the workspace name,
      // we return "Workspace" here. This will be updated once we create a separate page for hardware licenses.
      return "Workspace";
    }
    default: {
      return "";
    }
  }
}

/** Convert the backend provider ID to a frontend provider string */
export function convertProviderIdToProvider(
  providerId: FaroCoreApiTypes.UserProviderId
): FaroCoreApiTypes.AdminAuthMethod {
  switch (providerId) {
    case "APPLE":
      return "apple";
    case "GOOGLE":
      return "google";
    case "AUTODESK":
      return "autodesk";
    case "PLANGRID":
      return "plangrid";
    case "PROCORE":
      return "procore";
    case "FARO-SPHERE":
      return "faro-sphere";
    case "email":
      return "email";
    case "GENERIC_OAUTH":
      return "company-sso";

    default:
      throw new Error(`No matching provider for ${providerId}`);
  }
}

/** Format a date if exist, otherwise return "never" */
export function optionalDate({ date }: { date?: DateTimeInput }): string {
  if (!date) {
    return "never";
  }

  return DateTimeUtils.formatDateTimeInUtc({
    date,
  });
}
